import { useEffect, useState, type MouseEvent } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAo3Site } from "@/hooks/ao3SiteContext";
import { ao3ScrollToSection } from "@/hooks/ao3ScrollToSection";
import { ao3Assets, ao3NavItems } from "@/mocks/ao3Home";
import Ao3Toggles from "./Ao3Toggles";

export default function Ao3Header() {
  const { t } = useTranslation();
  const { config, openDownload } = useAo3Site();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const solid = scrolled || menuOpen;
  const tone: "overlay" | "solid" = solid ? "solid" : "overlay";

  const go = (e: MouseEvent, target: string) => {
    e.preventDefault();
    setMenuOpen(false);
    ao3ScrollToSection(target);
  };

  return (
    <header
      id="ao3-header"
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        solid ? "border-b border-background-200/70 bg-background-50/95 backdrop-blur" : "bg-transparent"
      }`}
    >
      <div className={`flex items-center justify-between gap-4 px-4 md:px-6 lg:px-10 h-16 md:h-[72px] ${solid ? "text-foreground-950" : "text-background-50 dark:text-foreground-950"}`}>
        <Link
          to="/"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-2.5 cursor-pointer"
        >
          <span className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-md bg-background-50">
            <img src={ao3Assets.logo} alt="AO3 Reader logo" title="AO3 Reader" className="h-8 w-8 object-contain" />
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-lg font-semibold">{t("brand.short")}</span>
            <span className={`text-[10px] font-medium tracking-[0.18em] ${solid ? "text-foreground-500" : "text-background-50/70 dark:text-foreground-950/70"}`}>
              READER
            </span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {ao3NavItems.map((item) => (
            <a
              key={item.id}
              href={`#${item.target}`}
              onClick={(e) => go(e, item.target)}
              className={`cursor-pointer rounded-md px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
                solid ? "text-foreground-700 hover:bg-background-200/70 hover:text-foreground-950" : "text-background-50/90 hover:bg-background-50/15 dark:text-foreground-950/90 dark:hover:bg-foreground-950/15"
              }`}
            >
              {t(`nav.${item.id}`)}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <div className="hidden md:flex items-center gap-2">
            <Ao3Toggles tone={tone} />
            <a
              href={config.loginUrl || "#"}
              className={`cursor-pointer rounded-md border px-3.5 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
                solid
                  ? "border-background-300/70 text-foreground-700 hover:bg-background-200/70"
                  : "border-background-50/40 text-background-50 hover:bg-background-50/15 dark:border-foreground-950/40 dark:text-foreground-950 dark:hover:bg-foreground-950/15"
              }`}
            >
              {t("nav.login")}
            </a>
            <button
              type="button"
              onClick={openDownload}
              className="cursor-pointer rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold whitespace-nowrap text-background-50 transition-colors hover:bg-primary-600 dark:text-foreground-950"
            >
              {t("nav.download")}
            </button>
          </div>

          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Toggle menu"
            className={`flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border lg:hidden ${
              solid ? "border-background-300/70 text-foreground-800" : "border-background-50/40 text-background-50 dark:border-foreground-950/40 dark:text-foreground-950"
            }`}
          >
            <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-xl leading-none`} />
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="lg:hidden border-t border-background-200/70 bg-background-50 px-4 py-4 md:px-6">
          <nav className="grid gap-1">
            {ao3NavItems.map((item) => (
              <a
                key={item.id}
                href={`#${item.target}`}
                onClick={(e) => go(e, item.target)}
                className="cursor-pointer rounded-md px-3 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-200/70"
              >
                {t(`nav.${item.id}`)}
              </a>
            ))}
          </nav>
          <div className="mt-4 flex flex-col gap-3">
            <Ao3Toggles tone="solid" />
            <div className="flex items-center gap-2">
              <a
                href={config.loginUrl || "#"}
                className="flex-1 cursor-pointer rounded-md border border-background-300/70 px-4 py-2.5 text-center text-sm font-medium text-foreground-700 transition-colors hover:bg-background-200/70"
              >
                {t("nav.login")}
              </a>
              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  openDownload();
                }}
                className="flex-1 cursor-pointer rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 dark:text-foreground-950"
              >
                {t("nav.download")}
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}